import React from 'react';
import { InfoCard, ResponseErrorPanel } from '@backstage/core-components';
import {
  EntityRefLink,
  catalogApiRef,
  entityPresentationSnapshot,
  useEntity,
  useRelatedEntities,
} from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import {
  Entity,
  RELATION_CHILD_OF,
  RELATION_MEMBER_OF,
  UserEntity,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { Box, Grid, Typography, makeStyles } from '@material-ui/core';
import { VirtualChip, isVirtual } from '../../input/VirtualPicker';
import {
  ServiceProviderChip,
  isServiceProvider,
} from '../../input/ServiceProvidersOnlyPicker';

/** How far one level of the tree is pushed in, in pixels. */
const INDENT_PER_LEVEL = 16;

/**
 * The contact lists the CMDB stamps onto departments and teams, in the order
 * the tags are shown. `accountableContact` holds a single contact, the other
 * three hold lists; `contactsOf` reads both shapes the same way.
 */
const ROLES = [
  { field: 'accountableContact', label: 'accountable' },
  { field: 'mainContacts', label: 'main' },
  { field: 'technicalContacts', label: 'technical' },
  { field: 'securityContacts', label: 'security' },
] as const;

/**
 * The technical Entra groups of a department or a team, which the CMDB names
 * by their role. The person's own group comes first on a row, ahead of any
 * contact role, editor above viewer.
 */
const ENTRA_ROLES = ['editor', 'viewer'] as const;

/** A technical group as `spec.cmdbGroups.technical` lists it. */
type CmdbTechnicalGroup = { name: string; full_name?: string | null };

/**
 * All a node is read for is its place in the tree, its label, whether the
 * CMDB has wound it up or calls it virtual - an entity that comes back without
 * its tags would pass for active and for a unit of the line organisation - and
 * whether it is a service provider, which its labels say.
 */
const TREE_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'metadata.tags',
  'metadata.labels',
  'spec.type',
  'spec.parent',
];

const useStyles = makeStyles(theme => ({
  // The profile card next to this one draws its links inside a `ListItemText`,
  // which puts them in `body1`; a card body inherits the smaller default.
  tree: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
  },
}));

/** A department or a team, and how deep it sits in the rendered tree. */
type OrgNode = {
  entity: Entity;
  depth: number;
};

type OrgTree = {
  nodes: OrgNode[];
  /** What the person is on a node, by entity name. Empty for an ancestor. */
  rolesByName: Map<string, string[]>;
};

/**
 * Where this person sits in the CMDB org tree and what the org has put their
 * name against: every department and team they are an editor, a viewer or a
 * contact of, in one tree, each row tagged with what they are there. A row
 * with no tag is only carried along because something below it has one.
 *
 * Editor and viewer are read off the DP Entra groups the person is really in,
 * the `memberOf` relation to an `ad-group`. Such a group is a child of the
 * department or team that declares it, which is what `childOf` leads back to,
 * and the role is the name that department or team gives it in
 * `spec.cmdbGroups.technical`. The team membership `memberOf` carries as well
 * is not used: `GroupProcessor` only mints it for a team, so a department
 * would never be tagged. The contact roles run the other way: the catalog
 * only knows them as lists on the
 * department or team, so they are read back with a filter on the contact
 * field. Those fields are indexed even though they hold arrays of objects: the
 * catalog flattens arrays into the search table without their indices, which
 * makes `spec.technicalContacts.entityRef` a key like any other.
 *
 * Only departments and teams are shown. An application can name a contact too,
 * but it hangs off no department and has no place in this tree.
 *
 * @public
 */
export const OrganizationCard = () => {
  const { entity: user } = useEntity<UserEntity>();
  const catalogApi = useApi(catalogApiRef);
  const classes = useStyles();
  const name = user.metadata.name;

  const {
    entities: groups,
    loading: loadingGroups,
    error: groupsError,
  } = useRelatedEntities(user, { type: RELATION_MEMBER_OF, kind: 'Group' });
  const entraGroups = (groups ?? []).filter(isEntraGroup);
  // The departments and teams those groups hang off. A custom group or one of
  // another Entra group leads somewhere too; which of them really declares the
  // group as its editor or viewer is only known once they are read.
  const ownerRefs = [
    ...new Set(
      entraGroups.flatMap(it =>
        (it.relations ?? [])
          .filter(relation => relation.type === RELATION_CHILD_OF)
          .map(relation => relation.targetRef),
      ),
    ),
  ].sort();
  const ownersKey = ownerRefs.join('\n');

  const {
    value: owners,
    loading: loadingOwners,
    error: ownersError,
  } = useAsync(async () => {
    if (ownerRefs.length === 0) {
      return { key: ownersKey, items: [] };
    }
    const { items } = await catalogApi.getEntitiesByRefs({
      entityRefs: ownerRefs,
      fields: [...TREE_FIELDS, 'spec.cmdbGroups.technical'],
    });
    return {
      key: ownersKey,
      // An archived team drops out here: the membership outlives the team
      // because the Entra groups behind it do, but the team is not somewhere
      // the person still sits.
      items: items
        .filter((it): it is Entity => it !== undefined && it !== null)
        .filter(isInOrgTree),
    };
  }, [catalogApi, ownersKey]);

  const { value, loading, error } = useAsync(async () => {
    const [contacts, departments] = await Promise.all([
      // A list of filters is an OR, so this is one round trip for all four
      // roles. Which of them matched is not in the answer, hence the contact
      // fields are read back and the roles are worked out below.
      catalogApi.getEntities({
        filter: ROLES.map(role => ({ [`spec.${role.field}.entityRef`]: name })),
        fields: [...TREE_FIELDS, ...ROLES.map(role => `spec.${role.field}`)],
      }),
      // The whole set in one call rather than a round trip per level: the
      // parent chain is only a handful of names but they are not known up
      // front, and there are a few hundred departments in total.
      catalogApi.getEntities({
        filter: { kind: 'Group', 'spec.type': 'department' },
        fields: TREE_FIELDS,
      }),
    ]);
    return {
      contacts: contacts.items.filter(isInOrgTree),
      departments: new Map(
        departments.items.map(it => [it.metadata.name, it] as const),
      ),
    };
  }, [catalogApi, name]);

  // Either lookup failing has to be said out loud. A tree drawn off half the
  // answer is the worse outcome: the contact rows still render, so it reads as
  // a complete account of where the person sits while every editor and viewer
  // tag is quietly missing.
  const failure = error ?? groupsError ?? ownersError;
  if (failure) {
    return (
      <Grid item xs={12} md={6}>
        <ResponseErrorPanel error={failure} />
      </Grid>
    );
  }

  const { nodes, rolesByName } = buildTree({
    groupOwners: owners?.items ?? [],
    entraGroupNames: new Set(entraGroups.map(it => it.metadata.name)),
    contacts: value?.contacts ?? [],
    departments: value?.departments ?? new Map(),
    name: name,
  });

  // A person the org tree says nothing about has no card, and nothing is drawn
  // while the answer is still out either - a card that appears and then
  // disappears again is worse than one that arrives a beat late. The owners
  // are only read once the memberships are in, so until their answer is for
  // the groups at hand it is still out, too.
  const ownersPending = loadingOwners || owners?.key !== ownersKey;
  if (loadingGroups || ownersPending || loading || nodes.length === 0) {
    return null;
  }

  return (
    <Grid item xs={12} md={6}>
      <InfoCard title="Organization" variant="gridItem">
        <Typography variant="body1" component="div" className={classes.tree}>
          {nodes.map(node => (
            <Box
              key={stringifyEntityRef(node.entity)}
              className={classes.row}
              style={{ paddingLeft: node.depth * INDENT_PER_LEVEL }}
            >
              {/* A department carries what hangs off it, so it is set apart
                  from the teams that do not. */}
              <Box
                component="span"
                fontWeight={isDepartment(node.entity) ? 700 : 400}
              >
                <EntityRefLink entityRef={node.entity} defaultKind="Group" />
              </Box>
              {/* Said the way the Teams page says it, ahead of the roles. */}
              {isServiceProvider(node.entity) && <ServiceProviderChip />}
              {isVirtual(node.entity) && <VirtualChip />}
              <Typography variant="caption" color="textSecondary">
                {(rolesByName.get(node.entity.metadata.name) ?? []).join(' · ')}
              </Typography>
            </Box>
          ))}
        </Typography>
      </InfoCard>
    </Grid>
  );
};

function isTeam(entity: Entity): boolean {
  return entity.spec?.type === 'team';
}

function isDepartment(entity: Entity): boolean {
  return entity.spec?.type === 'department';
}

function isEntraGroup(entity: Entity): boolean {
  return entity.spec?.type === 'ad-group';
}

/**
 * The technical group the entity gives the role, addressed the way the
 * provider names the group entity: by its `full_name`, or its name where the
 * CMDB has no full name for it.
 */
function technicalGroupName(entity: Entity, role: string): string | undefined {
  const technical: CmdbTechnicalGroup[] =
    (entity.spec as any)?.cmdbGroups?.technical ?? [];
  const group = technical.find(it => it.name === role);
  return group ? (group.full_name ?? group.name) : undefined;
}

/** Whether the CMDB has wound the entity up, as the provider tags it. */
function isArchived(entity: Entity): boolean {
  return entity.metadata.tags?.includes('archived') ?? false;
}

/**
 * The kinds the tree is built of - anything else has no place in it. An
 * archived team is dropped here too; a department is not, because it is only
 * ever drawn to carry what hangs off it, and a live team under a wound-up
 * department still has to be reachable.
 */
function isInOrgTree(entity: Entity): boolean {
  if (entity.kind !== 'Group') {
    return false;
  }
  return isDepartment(entity) || (isTeam(entity) && !isArchived(entity));
}

function parentOf(entity: Entity): string | undefined {
  return entity.spec?.parent as string | undefined;
}

function titleOf(entity: Entity): string {
  return entityPresentationSnapshot(entity).primaryTitle;
}

/**
 * The contacts an entity holds in the given field, as a list whether the field
 * carries one contact or many.
 */
function contactsOf(
  entity: Entity,
  field: string,
): ({ entityRef?: string } | undefined)[] {
  const value = (entity.spec as any)?.[field];
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

/**
 * The entity with every department above it, root first. The chain stops at
 * the first parent the catalog has no department for, so a team hanging off
 * nothing is a path of its own.
 */
function pathOf(entity: Entity, departments: Map<string, Entity>): Entity[] {
  const path = [entity];
  // A parent chain that loops in the CMDB must not spin here, so every name is
  // only walked once.
  const seen = new Set([entity.metadata.name]);
  let parent = parentOf(entity);
  while (parent && !seen.has(parent) && departments.has(parent)) {
    seen.add(parent);
    const department = departments.get(parent)!;
    path.unshift(department);
    parent = parentOf(department);
  }
  return path;
}

/**
 * The paths of everything the person is on merged into one tree, flattened
 * into the order it is rendered in. A department shared by two teams is one
 * row with both teams below it, not one row per team - and a department the
 * person is a contact of is that same row, tagged, not a second one.
 */
export function buildTree(input: {
  /** The departments and teams the person's Entra groups hang off. */
  groupOwners: Entity[];
  /** The names of the Entra groups the person is in. */
  entraGroupNames: Set<string>;
  contacts: Entity[];
  departments: Map<string, Entity>;
  name: string;
}): OrgTree {
  const { groupOwners, entraGroupNames, contacts, departments, name } = input;

  const rolesByName = new Map<string, string[]>();
  const addRole = (entity: Entity, role: string) => {
    const roles = rolesByName.get(entity.metadata.name) ?? [];
    rolesByName.set(entity.metadata.name, [...roles, role]);
  };
  // Entity names compare case-insensitively in the catalog, and the CMDB and
  // Entra need not agree on the case of a group name.
  const inGroup = new Set(
    [...entraGroupNames].map(it => it.toLocaleLowerCase('en-US')),
  );
  // An owner reached through a custom group only, or through none of its
  // technical groups at all, is nothing the person is on.
  const memberships = groupOwners.filter(entity => {
    let tagged = false;
    for (const role of ENTRA_ROLES) {
      const group = technicalGroupName(entity, role);
      if (group && inGroup.has(group.toLocaleLowerCase('en-US'))) {
        addRole(entity, role);
        tagged = true;
      }
    }
    return tagged;
  });
  for (const entity of contacts) {
    for (const role of ROLES) {
      // A filter matches the whole entity, so an entity that names the person
      // as one contact comes back carrying its other contacts too. Only the
      // fields that name this person turn into a tag.
      const named = contactsOf(entity, role.field).some(
        contact => contact?.entityRef === name,
      );
      if (named) {
        addRole(entity, role.label);
      }
    }
  }

  const byName = new Map<string, Entity>();
  const childrenOf = new Map<string, Set<string>>();
  const roots = new Set<string>();

  for (const entity of [...memberships, ...contacts]) {
    pathOf(entity, departments).forEach((step, index, path) => {
      const stepName = step.metadata.name;
      // First writer wins: every read projects the fields the tree needs, so
      // whichever copy of an entity comes first is as good as the others.
      if (!byName.has(stepName)) {
        byName.set(stepName, step);
      }
      const parent = path[index - 1];
      if (!parent) {
        roots.add(stepName);
        return;
      }
      const siblings = childrenOf.get(parent.metadata.name) ?? new Set();
      siblings.add(stepName);
      childrenOf.set(parent.metadata.name, siblings);
    });
  }

  const sorted = (names: Set<string> | undefined): string[] =>
    [...(names ?? [])].sort((a, b) =>
      titleOf(byName.get(a)!).localeCompare(titleOf(byName.get(b)!), 'en-US'),
    );

  const nodes: OrgNode[] = [];
  // `pathOf` breaks a parent chain that loops, but two nodes of the same loop
  // can still end up as each other's child here - so every name is drawn once
  // and the walk terminates whatever the CMDB says.
  const drawn = new Set<string>();
  const visit = (names: Set<string> | undefined, depth: number) => {
    for (const stepName of sorted(names)) {
      if (drawn.has(stepName)) {
        continue;
      }
      drawn.add(stepName);
      nodes.push({ entity: byName.get(stepName)!, depth: depth });
      visit(childrenOf.get(stepName), depth + 1);
    }
  };
  visit(roots, 0);

  return { nodes: nodes, rolesByName: rolesByName };
}
